// Used in for bulk copy
import memoizeOne from 'memoize-one';
import { dateTimeStringValueHandler } from '@atlassian/jira-business-fields/src/common/field-types/datetime/value/index.tsx';
import { goalStringValueHandler } from '@atlassian/jira-business-fields/src/common/field-types/goals/value/index.tsx';
import {
	cascadingSelectRichValueHandler,
	cascadingSelectStringValueHandler,
} from '../field-types/cascading-select/value';
import {
	categoryRichValueHandler,
	categoryStringValueHandler,
} from '../field-types/category/value';
import {
	checkboxRichValueHandler,
	checkboxStringValueHandler,
} from '../field-types/checkbox/value';
import {
	componentsRichValueHandler,
	componentsStringValueHandler,
} from '../field-types/components/value';
import { dateStringValueHandler } from '../field-types/date/value';
import { devSummaryStringValueHandler } from '../field-types/dev-summary/value';
import { groupRichValueHandler, groupStringValueHandler } from '../field-types/group/value';
import { keyStringValueHandler } from '../field-types/key/value';
import { labelsStringValueHandler } from '../field-types/labels/value';
import {
	multiSelectRichValueHandler,
	multiSelectStringValueHandler,
} from '../field-types/multi-select/value';
import { numberStringValueHandler } from '../field-types/number/value';
import { parentStringValueHandler, parentRichValueHandler } from '../field-types/parent/value';
import {
	priorityRichValueHandler,
	priorityStringValueHandler,
} from '../field-types/priority/value';
import { projectRichValueHandler, projectStringValueHandler } from '../field-types/project/value';
import {
	radioSelectRichValueHandler,
	radioSelectStringValueHandler,
} from '../field-types/radio-select/value';
import { resolutionStringValueHandler } from '../field-types/resolution/value';
import { richTextStringValueHandler } from '../field-types/rich-text/value';
import {
	singleSelectRichValueHandler,
	singleSelectStringValueHandler,
} from '../field-types/single-select/value';
import { sprintRichValueHandler, sprintStringValueHandler } from '../field-types/sprint/value';
import { statusCategoryStringValueHandler } from '../field-types/status-category/value';
import { statusStringValueHandler } from '../field-types/status/value';
import { teamRichValueHandler, teamStringValueHandler } from '../field-types/team/value';
import { textStringValueHandler } from '../field-types/text/value';
import { timeTrackingStringValueHandler } from '../field-types/time-tracking/value';
import { typeStringValueHandler } from '../field-types/type/value';
import { urlStringValueHandler } from '../field-types/url/value';
import { userStringValueHandler, userRichValueHandler } from '../field-types/user/value';
import { versionStringValueHandler, versionRichValueHandler } from '../field-types/version/value';
import type { ListIssueFieldTransformed } from '../types';
import type { StringValue, RichValue } from './types';

const createStringValueHandlers = memoizeOne(() => ({
	...categoryStringValueHandler,
	...checkboxStringValueHandler,
	...componentsStringValueHandler,
	...versionStringValueHandler,
	...parentStringValueHandler,
	...devSummaryStringValueHandler,
	...labelsStringValueHandler,
	...numberStringValueHandler,
	...keyStringValueHandler,
	...typeStringValueHandler,
	...userStringValueHandler,
	...teamStringValueHandler,
	...groupStringValueHandler,
	...resolutionStringValueHandler,
	...dateTimeStringValueHandler,
	...dateStringValueHandler,
	...goalStringValueHandler,
	...textStringValueHandler,
	...priorityStringValueHandler,
	...radioSelectStringValueHandler,
	...urlStringValueHandler,
	...multiSelectStringValueHandler,
	...statusStringValueHandler,
	...statusCategoryStringValueHandler,
	...timeTrackingStringValueHandler,
	...singleSelectStringValueHandler,
	...sprintStringValueHandler,
	...richTextStringValueHandler,
	...projectStringValueHandler,
	...cascadingSelectStringValueHandler,
}));

export const getStringValue = (
	fieldType: keyof ReturnType<typeof createStringValueHandlers>,
	...args: Parameters<StringValue<ListIssueFieldTransformed>>
): ReturnType<StringValue<ListIssueFieldTransformed>> | undefined => {
	const handlers = createStringValueHandlers();

	if (handlers[fieldType] == null) {
		return;
	}
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return (handlers[fieldType] as StringValue<ListIssueFieldTransformed>)?.(...args);
};

export const isStringValueSupported = (
	fieldType: string,
): fieldType is keyof ReturnType<typeof createStringValueHandlers> => {
	const handlers = createStringValueHandlers();
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return handlers[fieldType as keyof ReturnType<typeof createStringValueHandlers>] != null;
};

const createRichValueHandlers = memoizeOne(() => ({
	...categoryRichValueHandler,
	...checkboxRichValueHandler,
	...componentsRichValueHandler,
	...userRichValueHandler,
	...versionRichValueHandler,
	...priorityRichValueHandler,
	...multiSelectRichValueHandler,
	...singleSelectRichValueHandler,
	...sprintRichValueHandler,
	...parentRichValueHandler,
	...teamRichValueHandler,
	...projectRichValueHandler,
	...groupRichValueHandler,
	...cascadingSelectRichValueHandler,
	...radioSelectRichValueHandler,
}));

export const isRichValueSupported = (
	fieldType:
		| keyof ReturnType<typeof createRichValueHandlers>
		| keyof ReturnType<typeof createStringValueHandlers>,
): fieldType is keyof ReturnType<typeof createRichValueHandlers> => {
	const handlers = createRichValueHandlers();
	if (fieldType in handlers) {
		return true;
	}
	return false;
};

type RichValueGetterValue = RichValue<
	ListIssueFieldTransformed,
	ListIssueFieldTransformed['value']
>;

export const getRichOrStringValue = (
	fieldType:
		| keyof ReturnType<typeof createRichValueHandlers>
		| keyof ReturnType<typeof createStringValueHandlers>,
	...args: Parameters<RichValueGetterValue>
): ReturnType<RichValueGetterValue | typeof getStringValue> | undefined => {
	const handlers = createRichValueHandlers();

	if (isRichValueSupported(fieldType)) {
		return (
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(handlers[fieldType] as unknown as RichValueGetterValue)?.(...args)
		);
	}
	return getStringValue(fieldType, ...args);
};
